import { Routes } from '@angular/router';
import { createRoleGuard } from './core/guards/role.guard';
import { createRoleMatchGuard } from './core/guards/role-match.guard';
import { createPrivilegeGuard, createRoleBlockGuard } from './core/guards/privilege.guard';
import { UserRole, ALL_AUTHENTICATED_ROLES, ADMIN_UI_ROLES } from './core/models/role.enum';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';

// Import dashboard components
import { OrganizationDashboardComponent } from './pages/organization/pages/dashboard/dashboard.component';
import { TelecallerDashboardComponent } from './pages/telecaller/pages/dashboard/dashboard.component';
import { CallLogsComponent as OrgCallLogsComponent } from './pages/organization/pages/call-logs/call-logs.component';
import { CallLogsComponent as TelCallLogsComponent } from './pages/telecaller/pages/call-logs/call-logs.component';

export const routes: Routes = [
  // Reset Password Route
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/auth/reset-password/reset-password.component').then(
        (m) => m.ResetPasswordComponent
      ),
  },

  // Medjourney UI Control Kit (Calm Clinical)
  {
    path: 'ui-kit',
    loadComponent: () =>
      import('./pages/ui-kit/ui-kit.component').then((m) => m.UiKitComponent),
  },
  {
    path: 'ui-control-kit',
    redirectTo: 'ui-kit',
    pathMatch: 'full',
  },
  {
    path: 'preview-patient-report',
    loadComponent: () =>
      import('./shared/components/patient/patient-report/patient-report').then((m) => m.PatientReportComponent),
  },

  // Public Routes
  {
    path: '',
    loadChildren: () =>
      import('./pages/public/public-routing.module').then((m) => m.PublicRoutingModule),
  },

  // Auth Routes
  {
    path: 'auth',
    loadChildren: () => import('./pages/auth/auth.module').then((m) => m.AuthModule),
  },

  // Protected Routes with Unified Layout
  {
    path: '',
    component: AppLayoutComponent,
    // ALL 9 roles (+ legacy ORGANIZATION/ADMIN) can enter the protected shell
    canActivate: [createRoleGuard(ALL_AUTHENTICATED_ROLES)],
    children: [
      // Access denied - rendered inside the layout in place of the blocked page
      {
        path: 'access-denied',
        loadComponent: () =>
          import('./pages/access-denied/access-denied.component').then((m) => m.AccessDeniedComponent),
      },

      /**
       * SHARED ROUTES
       * Use 'canMatch' to selectively load components based on role.
       * If guard returns false, Router skips to the next matching sibling route.
       */
      
      // Dashboard - Telecaller
      {
        path: 'dashboard',
        component: TelecallerDashboardComponent,
        canMatch: [createRoleMatchGuard([UserRole.TELECALLER])],
      },
      // Dashboard - All other authenticated roles → Org dashboard (requires view_dashboard privilege)
      // PHARMACIST is hard-blocked and redirected to /patient regardless of privilege settings
      {
        path: 'dashboard',
        component: OrganizationDashboardComponent,
        canMatch: [createRoleMatchGuard(ADMIN_UI_ROLES)],
        canActivate: [
          createRoleBlockGuard([UserRole.PHARMACIST], '/patient'),
          createPrivilegeGuard(['view_dashboard'], undefined, true),
        ],
      },

      // Call Logs - Telecaller
      {
        path: 'call-logs',
        component: TelCallLogsComponent,
        canMatch: [createRoleMatchGuard([UserRole.TELECALLER])],
      },
      // Call Logs - All admin-UI roles
      {
        path: 'call-logs',
        component: OrgCallLogsComponent,
        canMatch: [createRoleMatchGuard(ADMIN_UI_ROLES)],
        canActivate: [createPrivilegeGuard(['view_followups', 'log_followups'])],
      },

      /**
       * MODULE ROUTES
       * These load the remaining role-specific routes.
       */

      // Organization Routing Module — all non-telecaller roles
      {
        path: '',
        canMatch: [createRoleMatchGuard(ADMIN_UI_ROLES)],
        loadChildren: () =>
          import('./pages/organization/organization-routing.module').then(
            (m) => m.OrganizationRoutingModule
          ),
      },
      
      // Telecaller Routing Module
      {
        path: '',
        canMatch: [createRoleMatchGuard([UserRole.TELECALLER])],
        loadChildren: () =>
          import('./pages/telecaller/telecaller-routing.module').then(
            (m) => m.TelecallerRoutingModule
          ),
      },
    ],
  },

  // Wildcard route - 404 page
  {
    path: '**',
    redirectTo: 'home',
  },
];
